import type { FC, ReactNode } from 'react'
import type { PostCard as PostCardType } from '@/types'

import { Star } from 'lucide-react'

import { cn } from '@/lib/utils'
import { PostCard } from '@/components/content/post-card'

interface FeaturedArticlesProps {
    posts: PostCardType[]
    pillarName: string
    className?: string
}

// The curated pick from a pillar's cluster set, surfaced directly under the
// takeaways box so the strongest articles are reachable before the long guide
// body. Every card is a real link in the initial HTML (a horizontal scroller on
// small screens rather than a JS carousel), so nothing here is hidden from
// crawlers or answer engines.
export const FeaturedArticles: FC<FeaturedArticlesProps> = ({
    posts,
    pillarName,
    className
}): ReactNode => {
    if (posts.length === 0) return null

    return (
        <section
            aria-labelledby='featured-articles-heading'
            className={cn('border-t border-border pt-10', className)}
        >
            <div className='flex items-center gap-2'>
                <Star className='size-4 text-primary' />
                <h2
                    id='featured-articles-heading'
                    className='font-heading text-lg font-semibold tracking-tight text-foreground'
                >
                    Start with these
                </h2>
            </div>

            <p className='mt-2 text-sm text-muted-foreground'>
                The most useful places to begin on {pillarName.toLowerCase()}.
            </p>

            <div
                className={cn(
                    'mt-8 grid gap-8',
                    'max-md:snap-x max-md:snap-mandatory max-md:auto-cols-[85%] max-md:grid-flow-col max-md:overflow-x-auto max-md:pb-4',
                    'md:grid-cols-3'
                )}
            >
                {posts.map((post) => (
                    <PostCard key={post.id} post={post} className='max-md:snap-start' />
                ))}
            </div>
        </section>
    )
}
